import { useMutation, useQueryClient } from "@tanstack/react-query";

import { $api, fetchClient, invalidate, orderingParam } from "./client";
import type { components } from "./schema";

export type User = components["schemas"]["UserAdmin"];

export interface UserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  isStaff?: boolean;
}

/**
 * The tenant's users. Admin-only, unpaginated. `search` matches email or
 * either name; `ordering` takes the camelCase field name with an optional `-`.
 */
export const useUsers = ({
  search,
  ordering,
}: { search?: string; ordering?: string } = {}) =>
  $api.useQuery("get", "/api/v1/users/", {
    params: { query: { search, ordering: orderingParam(ordering) } },
  });

const invalidateUsers = (queryClient: ReturnType<typeof useQueryClient>) =>
  invalidate(queryClient, "/api/v1/users/");

/**
 * The response carries `temporaryPassword` — the only time it is ever readable.
 * The admin relays it to the new user; it is not stored anywhere.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserInput) =>
      (await fetchClient.POST("/api/v1/users/", { body })).data!,
    onSuccess: () => invalidateUsers(queryClient),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number } & Partial<UserInput>) =>
      (
        await fetchClient.PATCH("/api/v1/users/{id}/", {
          params: { path: { id } },
          body,
        })
      ).data!,
    onSuccess: () => invalidateUsers(queryClient),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchClient.DELETE("/api/v1/users/{id}/", { params: { path: { id } } }),
    onSuccess: () => invalidateUsers(queryClient),
  });
}

/** Issues a fresh temporary password, invalidating the user's current one. */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: async (id: number) =>
      (
        await fetchClient.POST("/api/v1/users/{id}/reset-password/", {
          params: { path: { id } },
        })
      ).data!,
  });
}
